import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Camera, Check, Plus, Printer, Trash2, X } from 'lucide-react'
import { api } from '../lib/api'
import { Card, CardContent, CardHeader } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { Input } from '../components/ui/Input'
import { Badge } from '../components/ui/Badge'
import { useAuth } from '../contexts/auth'
import {
  ENTRY_MODE_HINT, ENTRY_MODE_LABEL, apiError, printVolumeLabels, resizeImage,
  type EntryMode, type IndustryTenant, type Location, type MaterialRow, type Supplier,
} from '../lib/industria'

const selectClass = 'h-10 w-full rounded-lg border border-gray-300 bg-white px-3 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/60'
const ENTRY_MODES: EntryMode[] = ['remessa', 'compra_ws', 'compra_por_conta']

type ReceiveResult = {
  lotId: string | null
  lotNumber: string | null
  totalQty: number
  volumes: { id: string; code: string; quantity: number }[]
  photos: number
}

export function IndustriaRecebimentoPage() {
  const queryClient = useQueryClient()
  // Custo é do admin (INDUSTRIA.md §7). A API já corta na resposta; aqui é para
  // não pedir ao operador um número que ele não deveria manusear.
  const { user } = useAuth()
  const canSeeCost = user?.role === 'admin'

  const [materialId, setMaterialId] = useState('')
  const [entryMode, setEntryMode] = useState<EntryMode>('remessa')
  const [ownerTenantId, setOwnerTenantId] = useState('')
  const [supplierId, setSupplierId] = useState('')
  const [docNumber, setDocNumber] = useState('')
  const [docKey, setDocKey] = useState('')
  const [docValue, setDocValue] = useState('')
  const [unitCost, setUnitCost] = useState('')
  const [lotNumber, setLotNumber] = useState('')
  const [supplierLot, setSupplierLot] = useState('')
  const [expiryDate, setExpiryDate] = useState('')
  const [locationId, setLocationId] = useState('')
  const [notes, setNotes] = useState('')
  const [photos, setPhotos] = useState<string[]>([])

  const [perVolume, setPerVolume] = useState('')
  const [volumeCount, setVolumeCount] = useState('')
  const [volumes, setVolumes] = useState<number[]>([])

  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<ReceiveResult | null>(null)
  const [printing, setPrinting] = useState(false)
  const [newSupplier, setNewSupplier] = useState('')

  const { data: materials = [] } = useQuery<MaterialRow[]>({
    queryKey: ['industria', 'materials', 'all'],
    queryFn: async () => (await api.get('/materials')).data,
  })
  const { data: tenants = [] } = useQuery<IndustryTenant[]>({
    queryKey: ['industria', 'tenants'],
    queryFn: async () => (await api.get('/materials/tenants')).data,
  })
  const { data: suppliers = [] } = useQuery<Supplier[]>({
    queryKey: ['industria', 'suppliers'],
    queryFn: async () => (await api.get('/suppliers')).data,
  })
  const { data: locations = [] } = useQuery<Location[]>({
    queryKey: ['stock', 'locations'],
    queryFn: async () => (await api.get('/stock/locations')).data,
  })

  const material = materials.find(m => m.id === materialId)
  const total = volumes.reduce((acc, q) => acc + (q || 0), 0)
  // Material exclusivo já traz o dono; catálogo compartilhado recebe o dono aqui.
  const ownerLocked = !!material?.ownerTenantId
  const effectiveOwner = material?.ownerTenantId ?? (entryMode === 'compra_ws' ? '' : ownerTenantId)
  const isPurchase = entryMode !== 'remessa'

  const problem = useMemo(() => {
    if (!materialId) return 'Escolha o material'
    if (entryMode !== 'compra_ws' && !effectiveOwner) return 'Remessa e compra por conta exigem o cliente dono'
    if (entryMode === 'compra_ws' && effectiveOwner) return 'Compra da WS não pode ter dono — use compra por conta'
    if (!volumes.length) return 'Monte ao menos um volume'
    if (volumes.some(q => !q || q <= 0)) return 'Todo volume precisa de quantidade'
    return null
  }, [materialId, entryMode, effectiveOwner, volumes])

  const supplierMutation = useMutation({
    mutationFn: async (name: string) => (await api.post('/suppliers', { name })).data as Supplier,
    onSuccess: (s) => {
      queryClient.invalidateQueries({ queryKey: ['industria', 'suppliers'] })
      setSupplierId(s.id)
      setNewSupplier('')
    },
    onError: (e) => setError(apiError(e, 'Não foi possível criar o fornecedor')),
  })

  const receiveMutation = useMutation({
    mutationFn: async () => (await api.post('/materials/receive', {
      materialId,
      ownerTenantId: effectiveOwner || null,
      entryMode,
      lotNumber: lotNumber.trim() || null,
      supplierLot: supplierLot.trim() || null,
      supplierId: supplierId || null,
      docNumber: docNumber.trim() || null,
      docKey: docKey.trim() || null,
      docValue: docValue ? Number(docValue) : null,
      unitCost: unitCost ? Number(unitCost) : null,
      expiryDate: expiryDate || null,
      locationId: locationId || null,
      volumes: volumes.map(quantity => ({ quantity })),
      notes: notes.trim() || null,
      photos: photos.length ? photos : undefined,
    })).data as ReceiveResult,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['industria', 'materials'] })
      queryClient.invalidateQueries({ queryKey: ['industria', 'volumes'] })
      setResult(data)
      resetForm()
    },
    onError: (e) => setError(apiError(e, 'Não foi possível registrar o recebimento')),
  })

  function resetForm() {
    setLotNumber(''); setSupplierLot(''); setExpiryDate('')
    setDocNumber(''); setDocKey(''); setDocValue(''); setUnitCost('')
    setNotes(''); setPhotos([]); setVolumes([]); setPerVolume(''); setVolumeCount('')
  }

  function buildVolumes() {
    const qty = Number(perVolume)
    const count = Number(volumeCount)
    if (!qty || !count || qty <= 0 || count <= 0) return
    setVolumes(Array.from({ length: Math.min(count, 500) }, () => qty))
  }

  async function addPhotos(files: FileList) {
    const list = [...files].slice(0, 10 - photos.length)
    const encoded = await Promise.all(list.map(f => resizeImage(f)))
    setPhotos(p => [...p, ...encoded])
  }

  async function handlePrint(format: 'label' | 'a4') {
    if (!result) return
    setPrinting(true)
    try {
      await printVolumeLabels(result.volumes.map(v => v.id), format)
    } catch (e) {
      setError(apiError(e, 'Não foi possível gerar as etiquetas'))
    } finally {
      setPrinting(false)
    }
  }

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-xl font-semibold text-gray-900">Recebimento de material</h2>
        <p className="text-sm text-gray-500">
          Toda entrada gera lote e volumes etiquetados — é onde a rastreabilidade nasce
        </p>
      </div>

      {result && (
        <Card className="border-green-200 bg-green-50/40">
          <CardHeader className="border-green-100">
            <span className="flex items-center gap-2 text-sm font-semibold text-green-800">
              <Check className="h-4 w-4" />
              Recebido — {result.totalQty.toLocaleString('pt-BR')} un em {result.volumes.length} volume(s)
              {result.lotNumber ? ` · lote ${result.lotNumber}` : ''}
              {result.photos ? ` · ${result.photos} foto(s)` : ''}
            </span>
            <div className="flex gap-2">
              <Button size="sm" variant="secondary" loading={printing} onClick={() => handlePrint('a4')}>
                <Printer className="h-4 w-4" /> A4
              </Button>
              <Button size="sm" loading={printing} onClick={() => handlePrint('label')}>
                <Printer className="h-4 w-4" /> Etiquetas
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setResult(null)}>
                <X className="h-4 w-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-2">
            {result.volumes.map(v => (
              <span key={v.id} className="rounded-lg border border-green-200 bg-white px-2.5 py-1 font-mono text-xs text-gray-700">
                {v.code} · {v.quantity}
              </span>
            ))}
          </CardContent>
        </Card>
      )}

      <div className="grid gap-5 lg:grid-cols-3">
        <div className="space-y-5 lg:col-span-2">
          <Card>
            <CardHeader><span className="text-sm font-semibold text-gray-900">1. O que chegou</span></CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Material</label>
                <select className={selectClass} value={materialId} onChange={e => { setMaterialId(e.target.value); setError(null) }}>
                  <option value="">Selecione...</option>
                  {materials.filter(m => m.active).map(m => (
                    <option key={m.id} value={m.id}>
                      {m.code} — {m.name}{m.ownerName ? ` (${m.ownerName})` : ''}
                    </option>
                  ))}
                </select>
                {material && (
                  <p className="mt-1 flex flex-wrap items-center gap-2 text-xs text-gray-500">
                    <Badge>{material.unit}</Badge>
                    {material.lotControlled ? <Badge variant="info">com lote</Badge> : <Badge>sem lote</Badge>}
                    saldo atual {material.quantity.toLocaleString('pt-BR')}
                    {material.unitsPerPack ? ` · pacote com ${material.unitsPerPack}` : ''}
                  </p>
                )}
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Como entrou</label>
                <div className="grid gap-2 sm:grid-cols-3">
                  {ENTRY_MODES.map(mode => (
                    <button
                      key={mode}
                      type="button"
                      onClick={() => { setEntryMode(mode); setError(null) }}
                      className={`rounded-xl border p-3 text-left transition-colors ${
                        entryMode === mode
                          ? 'border-blue-300 bg-blue-50/60 ring-1 ring-blue-200'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <span className="block text-sm font-medium text-gray-900">{ENTRY_MODE_LABEL[mode]}</span>
                      <span className="mt-1 block text-xs leading-snug text-gray-500">{ENTRY_MODE_HINT[mode]}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  Dono do material {entryMode === 'compra_ws' && <span className="text-gray-400">(a WS)</span>}
                </label>
                <select
                  className={selectClass}
                  value={effectiveOwner}
                  disabled={ownerLocked || entryMode === 'compra_ws'}
                  onChange={e => setOwnerTenantId(e.target.value)}
                >
                  <option value="">{entryMode === 'compra_ws' ? 'WS (próprio)' : 'Selecione o cliente...'}</option>
                  {tenants.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
                {ownerLocked && (
                  <p className="mt-1 text-xs text-gray-500">
                    Material exclusivo — o dono vem do cadastro e não muda aqui.
                  </p>
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <span className="text-sm font-semibold text-gray-900">2. Documento e lote</span>
            </CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-2">
              <Input label="Nº da nota" value={docNumber} onChange={e => setDocNumber(e.target.value)} />
              <Input label="Chave da NF-e" value={docKey} onChange={e => setDocKey(e.target.value)} />
              {isPurchase && canSeeCost && (
                <>
                  <Input
                    label="Valor da nota"
                    type="number"
                    step="0.01"
                    min={0}
                    value={docValue}
                    onChange={e => setDocValue(e.target.value)}
                  />
                  <Input
                    label="Custo unitário"
                    type="number"
                    step="0.0001"
                    min={0}
                    value={unitCost}
                    onChange={e => setUnitCost(e.target.value)}
                    placeholder="recalcula o custo médio"
                  />
                </>
              )}
              <Input
                label="Nº do lote"
                value={lotNumber}
                onChange={e => setLotNumber(e.target.value)}
                placeholder="em branco = gerado (AAMMDD-NN)"
                disabled={material ? !material.lotControlled : false}
              />
              <Input
                label="Lote do fornecedor"
                value={supplierLot}
                onChange={e => setSupplierLot(e.target.value)}
                disabled={material ? !material.lotControlled : false}
              />
              <Input
                label="Validade"
                type="date"
                value={expiryDate}
                onChange={e => setExpiryDate(e.target.value)}
              />
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Localização</label>
                <select className={selectClass} value={locationId} onChange={e => setLocationId(e.target.value)}>
                  <option value="">Sem endereço</option>
                  {locations.filter(l => l.active).map(l => (
                    <option key={l.id} value={l.id}>{l.code} — {l.zone}</option>
                  ))}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className="mb-1 block text-sm font-medium text-gray-700">Fornecedor</label>
                <div className="flex gap-2">
                  <select className={selectClass} value={supplierId} onChange={e => setSupplierId(e.target.value)}>
                    <option value="">Nenhum</option>
                    {suppliers.filter(s => s.active).map(s => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                </div>
                <div className="mt-2 flex gap-2">
                  <Input
                    placeholder="Cadastrar fornecedor novo"
                    value={newSupplier}
                    onChange={e => setNewSupplier(e.target.value)}
                  />
                  <Button
                    type="button"
                    variant="secondary"
                    disabled={!newSupplier.trim()}
                    loading={supplierMutation.isPending}
                    onClick={() => supplierMutation.mutate(newSupplier.trim())}
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <span className="text-sm font-semibold text-gray-900">3. Volumes</span>
              <span className="text-sm text-gray-500">
                total {total.toLocaleString('pt-BR')} {material?.unit ?? ''}
              </span>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-wrap items-end gap-3">
                <div className="w-36">
                  <Input
                    label="Qtd por volume"
                    type="number"
                    min={1}
                    value={perVolume}
                    onChange={e => setPerVolume(e.target.value)}
                  />
                </div>
                <div className="w-32">
                  <Input
                    label="Nº de volumes"
                    type="number"
                    min={1}
                    max={500}
                    value={volumeCount}
                    onChange={e => setVolumeCount(e.target.value)}
                  />
                </div>
                <Button type="button" variant="secondary" onClick={buildVolumes}>Montar</Button>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setVolumes(v => [...v, Number(perVolume) || 0])}
                >
                  <Plus className="h-4 w-4" /> Volume avulso
                </Button>
              </div>

              {volumes.length === 0 ? (
                <p className="rounded-lg border border-dashed border-gray-200 py-6 text-center text-sm text-gray-400">
                  Cada volume vira uma etiqueta com código de barras. Monte a divisão física da carga.
                </p>
              ) : (
                <div className="grid gap-2 sm:grid-cols-3 lg:grid-cols-4">
                  {volumes.map((qty, i) => (
                    <div key={i} className="flex items-center gap-1 rounded-lg border border-gray-200 px-2 py-1">
                      <span className="w-8 shrink-0 text-xs text-gray-400">#{i + 1}</span>
                      <input
                        type="number"
                        min={1}
                        className="w-full border-0 p-0 text-sm focus:outline-none focus:ring-0"
                        value={qty}
                        onChange={e => setVolumes(v => v.map((q, j) => (j === i ? Number(e.target.value) : q)))}
                      />
                      <button
                        type="button"
                        className="text-gray-300 hover:text-red-500"
                        onClick={() => setVolumes(v => v.filter((_, j) => j !== i))}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <div className="space-y-5">
          <Card>
            <CardHeader><span className="text-sm font-semibold text-gray-900">Fotos da entrada</span></CardHeader>
            <CardContent className="space-y-3">
              <p className="text-xs text-gray-500">
                Avaria fotografada agora é a prova depois. Até 10 fotos.
              </p>
              <label className="flex cursor-pointer items-center justify-center gap-2 rounded-lg border border-dashed border-gray-300 py-4 text-sm text-gray-600 hover:border-blue-300 hover:text-blue-600">
                <Camera className="h-4 w-4" /> Adicionar foto
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  multiple
                  className="hidden"
                  onChange={e => { if (e.target.files) addPhotos(e.target.files); e.target.value = '' }}
                />
              </label>
              {photos.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {photos.map((p, i) => (
                    <div key={i} className="relative">
                      <img src={p} alt="" className="h-16 w-16 rounded-lg object-cover" />
                      <button
                        type="button"
                        className="absolute -right-1 -top-1 rounded-full bg-white p-0.5 shadow ring-1 ring-gray-200"
                        onClick={() => setPhotos(ps => ps.filter((_, j) => j !== i))}
                      >
                        <X className="h-3 w-3 text-gray-500" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader><span className="text-sm font-semibold text-gray-900">Observações</span></CardHeader>
            <CardContent>
              <textarea
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/60"
                rows={3}
                value={notes}
                onChange={e => setNotes(e.target.value)}
                placeholder="2 caixas amassadas na chegada"
              />
            </CardContent>
          </Card>

          <Card>
            <CardContent className="space-y-3">
              {error && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}
              {problem && !error && <p className="text-sm text-gray-500">{problem}</p>}
              <Button
                className="w-full"
                size="lg"
                disabled={!!problem}
                loading={receiveMutation.isPending}
                onClick={() => { setError(null); receiveMutation.mutate() }}
              >
                Receber {total > 0 ? `${total.toLocaleString('pt-BR')} ${material?.unit ?? ''}` : ''}
              </Button>
              <p className="text-center text-xs text-gray-400">
                Falta cadastrar o material?{' '}
                <Link to="/industria/materiais" className="text-blue-600 hover:underline">Materiais</Link>
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}
